import { useState } from 'react';

export default function Home() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    // Validate required fields
    const name = formData.get('name') as string;
    const email = formData.get('email') as string;
    const message = formData.get('message') as string;
    
    if (!name || !email || !message) {
      setSubmitMessage('必須項目を入力してください。');
      setIsSubmitting(false);
      return;
    }

    if (message.length > 500) {
      setSubmitMessage('メッセージは500文字以内で入力してください。');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d36khnrtg2u3f2oj3tvg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any),
      });

      if (response.ok) {
        setSubmitMessage('お問い合わせを送信いたしました。ありがとうございます。');
        form.reset();
      } else {
        setSubmitMessage('送信に失敗しました。もう一度お試しください。');
      }
    } catch (error) {
      setSubmitMessage('送信に失敗しました。もう一度お試しください。');
    }

    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <h1>
                <img 
                  src="https://static.readdy.ai/image/4f6a1d7b5058e6da57f4192877d570f4/8397292e65571c64257096a9f31fb3e1.png" 
                  alt="合同会社サクラネクサスメディア" 
                  className="h-24"
                />
              </h1>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex space-x-8 items-center">
              <a href="#home" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer font-medium">ホーム</a>
              <a href="#about" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer font-medium">会社概要</a>
              <a href="#services" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer font-medium">サービス</a>
              <a href="#company" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer font-medium">会社情報</a>
              <a href="/blog" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer font-medium">ブログ</a>
              <a href="#contact" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer font-medium">お問い合わせ</a>
            </nav>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden w-6 h-6 flex items-center justify-center cursor-pointer"
            >
              <i className="ri-menu-line text-xl"></i>
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="md:hidden py-4 border-t">
              <nav className="flex flex-col space-y-2">
                <a href="#home" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer py-2">ホーム</a>
                <a href="#about" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer py-2">会社概要</a>
                <a href="#services" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer py-2">サービス</a>
                <a href="#company" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer py-2">会社情報</a>
                <a href="/blog" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer py-2">ブログ</a>
                <a href="#contact" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer py-2">お問い合わせ</a>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section with Background Image */}
      <section id="home" className="relative h-screen flex items-center justify-center"
        style={{
          backgroundImage: `url('https://readdy.ai/api/search-image?query=Modern%20professional%20business%20workspace%20with%20laptop%20computer%20on%20clean%20desk%20hands%20typing%20representing%20digital%20innovation%20and%20corporate%20excellence%20with%20soft%20natural%20lighting%20and%20minimalist%20aesthetic%20perfect%20for%20company%20homepage%20hero%20section&width=1920&height=1080&seq=business_hero_workspace&orientation=landscape')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/70 via-blue-800/50 to-transparent"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">合同会社サクラネクサスメディア</h2>
            <p className="text-xl md:text-2xl mb-8 font-light max-w-3xl mx-auto">
              革新的なデジタルソリューションで、お客様のビジネスを次のレベルへ導きます
            </p>
          </div>
        </div>
      </section>

      {/* Service Cards Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20business%20meeting%20room%20with%20modern%20conference%20table%20and%20chairs%20representing%20corporate%20consulting%20and%20strategic%20planning%20with%20clean%20minimalist%20design%20and%20natural%20lighting&width=400&height=250&seq=business_meeting_room&orientation=landscape"
                alt="サービス"
                className="w-full h-48 object-cover object-top"
              />
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">サービス</h3>
                <p className="text-gray-600 mb-4">
                  お客様のニーズに合わせた最適なソリューションを提供いたします
                </p>
                <a href="#services" className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium cursor-pointer">
                  詳しく見る
                  <i className="ri-arrow-right-line ml-1"></i>
                </a>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
              <img 
                src="https://readdy.ai/api/search-image?query=Modern%20corporate%20office%20building%20exterior%20with%20glass%20facade%20representing%20company%20headquarters%20and%20professional%20business%20establishment%20with%20blue%20sky%20background&width=400&height=250&seq=corporate_building&orientation=landscape"
                alt="会社情報"
                className="w-full h-48 object-cover object-top"
              />
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">会社情報</h3>
                <p className="text-gray-600 mb-4">
                  私たちの会社について詳しくご紹介いたします
                </p>
                <a href="#company" className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium cursor-pointer">
                  詳しく見る
                  <i className="ri-arrow-right-line ml-1"></i>
                </a>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20business%20consultation%20workspace%20with%20hands%20typing%20on%20laptop%20keyboard%20and%20documents%20on%20clean%20modern%20desk%20representing%20client%20services%20and%20corporate%20support%20with%20no%20visible%20faces%20for%20privacy%20protection%20and%20minimalist%20office%20environment&width=400&height=250&seq=consultation_workspace_privacy&orientation=landscape"
                alt="お問い合わせ"
                className="w-full h-48 object-cover object-top"
              />
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">お問い合わせ</h3>
                <p className="text-gray-600 mb-4">
                  ご質問やご相談がございましたらお気軽にお問い合わせください
                </p>
                <a href="#contact" className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium cursor-pointer">
                  お問い合わせ
                  <i className="ri-arrow-right-line ml-1"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">会社概要</h2>
          </div>
          
          <div className="max-w-4xl mx-auto text-gray-700 leading-relaxed space-y-6">
            <p>
              私たちは、デジタル技術を活用してお客様のビジネス成長をサポートする専門企業です。革新的なソリューションと質の高いサービスで、お客様の成功を実現いたします。
            </p>
            
            <div className="text-right mt-8">
              <p className="font-semibold">合同会社サクラネクサスメディア</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              <i className="ri-briefcase-line mr-3"></i>
              サービス
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white border border-gray-200 rounded-lg p-8 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded-lg mb-6 mx-auto">
                <i className="ri-computer-line text-blue-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">商品企画</h3>
              <p className="text-gray-600 text-center">
                お客様のアイデアを形にする商品企画サービスを提供いたします
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-8 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-lg mb-6 mx-auto">
                <i className="ri-tools-line text-green-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">OEM製造</h3>
              <p className="text-gray-600 text-center mb-4">
                高品質なOEM製品の製造を承っております
              </p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• アクリルキーホルダー</li>
                <li>• 缶バッジ</li>
                <li>• アクリルボード</li>
                <li>• オーロラアート</li>
              </ul>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-8 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 flex items-center justify-center bg-purple-100 rounded-lg mb-6 mx-auto">
                <i className="ri-shopping-cart-line text-purple-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">ECサイト運営</h3>
              <p className="text-gray-600 text-center">
                効果的なECサイトの構築・運営をサポートいたします
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* News Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              <i className="ri-information-line mr-3"></i>
              お知らせ
            </h2>
            <p className="text-gray-600">最新の情報をお届けいたします</p>
          </div>

          <div className="space-y-4 max-w-4xl mx-auto">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <span className="text-blue-600 font-medium">2025.11.15</span>
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">お知らせ</span>
                <span className="text-gray-900">ホームページを開設いたしました</span>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <span className="text-blue-600 font-medium">2025.11.01</span>
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">お知らせ</span>
                <span className="text-gray-900">会社設立のお知らせ</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Company Info Section */}
      <section id="company" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              <i className="ri-map-pin-line mr-3"></i>
              会社情報
            </h2>
            <p className="text-gray-600">東京都中央区</p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div>
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3241.0977!2d139.7648!3d35.6762!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x60188be701836fbb%3A0x3f5b3f8b8b8b8b8b!2z44CSMTAzLTAwMjcg5p2x5Lqs6YO95Lit5aSu5Yy66ZO25bqn77yR5LiB55uu77yR77yS4oiS77yU!5e0!3m2!1sja!2sjp!4v1733472000000!5m2!1sja!2sjp"
                width="100%" 
                height="400" 
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                className="rounded-lg shadow-lg"
              ></iframe>
            </div>
            
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">会社情報</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 flex items-center justify-center bg-blue-100 rounded-full mt-1">
                      <i className="ri-building-line text-blue-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">会社名</h4>
                      <p className="text-gray-600">合同会社サクラネクサスメディア</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 flex items-center justify-center bg-blue-100 rounded-full mt-1">
                      <i className="ri-calendar-line text-blue-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">設立</h4>
                      <p className="text-gray-600">2025年10月</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 flex items-center justify-center bg-blue-100 rounded-full mt-1">
                      <i className="ri-money-yen-circle-line text-blue-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">資本金</h4>
                      <p className="text-gray-600">150万円</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 flex items-center justify-center bg-blue-100 rounded-full mt-1">
                      <i className="ri-map-pin-line text-blue-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">本社所在地</h4>
                      <p className="text-gray-600">
                        〒103-0027 東京都中央区日本橋1丁目12-4
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">お問い合わせ</h1>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20business%20office%20desk%20with%20laptop%20computer%20notebook%20and%20coffee%20cup%20representing%20corporate%20consultation%20and%20client%20communication%20services%20with%20hands%20working%20on%20keyboard%20but%20no%20visible%20faces%20for%20privacy%20protection%20and%20clean%20modern%20workspace&width=600&height=400&seq=business_consultation_privacy&orientation=landscape"
                alt="お問い合わせ"
                className="w-full h-80 object-cover object-top rounded-lg shadow-lg"
              />
              <div className="mt-6">
                <p className="text-gray-700 text-lg">
                  ご質問やご相談がございましたら、お気軽にお問い合わせください。専門スタッフが丁寧に対応させていただきます。
                </p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-lg">
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">お問い合わせフォーム</h3>
              <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">お名前 <span className="text-red-500">必須</span></label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="山田太郎"
                  />
                </div>
                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">会社名</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="株式会社サンプル"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">メールアドレス <span className="text-red-500">必須</span></label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="example@email.com"
                  />
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">お問い合わせ種別</label>
                  <div className="relative">
                    <select
                      id="subject"
                      name="subject"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8 appearance-none"
                    >
                      <option value="">選択してください</option>
                      <option value="業務依頼">業務依頼</option>
                      <option value="商品に関するお問い合わせ">商品に関するお問い合わせ</option>
                      <option value="その他">その他</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <i className="ri-arrow-down-s-line text-gray-400"></i>
                    </div>
                  </div>
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">お問い合わせ内容 <span className="text-red-500">必須</span></label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    required
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                    placeholder="お問い合わせ内容をご記入ください"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">500文字以内でご記入ください</p>
                </div>
                
                {submitMessage && (
                  <div className={`p-3 rounded-lg text-sm ${submitMessage.includes('ありがとうございます') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {submitMessage}
                  </div>
                )}
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold py-3 px-6 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? '送信中...' : '送信する'}
                  <i className="ri-arrow-right-double-line ml-2"></i>
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Social Share */}
      <section className="py-8 bg-white border-t">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center space-x-4">
            <a 
              href="https://www.facebook.com/sharer/sharer.php?u=https://example.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-facebook-fill"></i>
              <span>Facebook</span>
            </a>
            <a 
              href="https://x.com/jSxYSzxhQo52199" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center space-x-2 bg-black hover:bg-gray-800 text-white px-4 py-2 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-twitter-x-fill"></i>
              <span>X (Twitter)</span>
            </a>
            <a 
              href="https://www.instagram.com/sakuranexusmedia/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center space-x-2 bg-gradient-to-r from-purple-500 to-pink-5 hover:from-purple-600 hover:to-pink-6 text-white px-4 py-2 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-instagram-fill"></i>
              <span>Instagram</span>
            </a>
            <a 
              href="https://b.hatena.ne.jp/add?mode=confirm&url=https://example.com&title=合同会社サクラネクサスメディア" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center space-x-2 bg-blue-800 hover:bg-blue-900 text-white px-4 py-2 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-bookmark-fill"></i>
              <span>はてな</span>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h4 className="text-lg font-semibold mb-4">クイックリンク</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#home" className="hover:text-white transition-colors cursor-pointer">ホーム</a></li>
                <li><a href="#about" className="hover:text-white transition-colors cursor-pointer">会社概要</a></li>
                <li><a href="#services" className="hover:text-white transition-colors cursor-pointer">サービス</a></li>
                <li><a href="#company" className="hover:text-white transition-colors cursor-pointer">会社情報</a></li>
                <li><a href="/blog" className="hover:text-white transition-colors cursor-pointer">ブログ</a></li>
                <li><a href="#contact" className="hover:text-white transition-colors cursor-pointer">お問い合わせ</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">会社情報</h4>
              <p className="text-gray-400 mb-4">
                合同会社サクラネクサスメディア
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">お問い合わせ</h4>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-start">
                  <i className="ri-map-pin-line mr-2 mt-1"></i>
                  <span>
                    〒103-0027 東京都中央区日本橋1丁目12-4
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 合同会社サクラネクサスメディア. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
